import type { ISetOperatorSettingsCmd } from "@/data/commands/user/SetOperatorSettingsCmd";
import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { FulfilmentTileSize, NewOrdersViewMode, OperatorPlaySoundMode, OrderStateMode, ProductionTileViewMode } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { ILocation } from "@/data/model/Location";
import type { IRoom } from "@/data/model/Room";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IFulfillmentGroupsQry } from "@/data/queries/catalog/FulfillmentGroupsQry";
import type { IRoomsQry } from "@/data/queries/room/RoomsQry";
import { Format } from "@/data/util/Format";
import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";



import { getLocationName } from "@/shared/helpers/LocationHelper";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";



import { Popup } from "@/components/dialog/Popup";



import { OperatorClient } from "@/operator/OperatorClient";
import { Select } from "@/operator/elements/Select";
import { trans } from "@/operator/localization/Trans";



import { OperatorBaseModal } from "./OperatorBaseModal";


export class SettingsModal extends OperatorBaseModal {
    private locationSelect: Select<ILocation>;
    private roomPlanSelect: Select<IRoom>;
    private orderRoomsSelect: Select<IRoom>;
    private fulfillmentGroupSelect: Select<IFulfillmentGroup>;
    private productionTileViewModeSelect: Select<ProductionTileViewLinkableEntity>;
    private newOrdersViewModeSelect: Select<NewOrdersViewLinkableEntity>;
    private fulfilmentTileSizeSelect: Select<FulfilmentTileSizeLinkableEntity>;
    private orderStateSelect: Select<OrderStateModeLinkableEntity>;
    private operatorPlaySoundModeSelector: Select<OperatorPlaySoundModeLinkableEntity>;

    private $form: JQuery;
    private $settings: JQuery;
    private isReady = false;

    private $actions: JQuery;
    private $cancelAction: JQuery;
    private $saveAction: JQuery;

    constructor() {
        super();

        this.$element.addClass("settings");
        this.beforeResize($(window).width(), $(window).height());

        this.$form = $('<form class="form" />').appendTo(this.$body);
        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSave();

            return false;
        });

        this.$settings = $('<div class="settings scrollable" />').appendTo(this.$form);

        const generalSection = $(
            '<div class="section"><div class="sectiontitle">' + trans.settings.secGeneral + "</div></div>"
        ).appendTo(this.$settings);
        let row = $('<div class="formrow"><div class="label">' + trans.settings.location + "</div></div>").appendTo(
            generalSection
        );
        this.locationSelect = new Select<ILocation>({
            optionalText: trans.settings.pleaseSelect,
            dataSource: {
                getData: () => Factory.getInstance<LocationDao>(LocationDao).getData({ limit: 999 }),
            },
            formatter: (m: ILocation | ILocation[]) => {
                if (Array.isArray(m)) {
                    return Format.toSimpleCommaSeparatedList(m, (c) => getLocationName(c));
                } else {
                    return getLocationName(m);
                }
            },
            lazyFetch: true,
        }).appendTo(row);
        this.locationSelect.onChange = (l: any) => this.onLocationChanged(l);

        const roomPlanSection = $(
            '<div class="section"><div class="sectiontitle">' + trans.settings.secRoomPlan + "</div></div>"
        ).appendTo(this.$settings);
        row = $('<div class="formrow"><div class="label">' + trans.settings.room + "</div></div>").appendTo(
            roomPlanSection
        );
        this.roomPlanSelect = new Select<IRoom>({
            dataSource: this.roomDataSource,
            formatter: (m: IRoom | IRoom[]) => {
                if (Array.isArray(m)) return Format.toSimpleCommaSeparatedList(m, (c) => c.name);

                return m.name;
            },
            lazyFetch: true,
        }).appendTo(row);

        const productionSection = $(
            '<div class="section"><div class="sectiontitle">' + trans.settings.secProduction + "</div></div>"
        ).appendTo(this.$settings);
        row = $('<div class="formrow"><div class="label">' + trans.settings.orderRooms + "</div></div>").appendTo(
            productionSection
        );
        this.orderRoomsSelect = new Select<IRoom>({
            dataSource: this.roomDataSource,
            optionalText: trans.general.all,
            formatter: (m) => {
                if (Array.isArray(m)) return Format.toSimpleCommaSeparatedList(m, (c) => c.name);

                return m.name;
            },
            isMultiSelect: true,
            lazyFetch: true,
        }).appendTo(row);

        row = $(
            '<div class="formrow"><div class="label">' + trans.settings.fulfillmentGroups + "</div></div>"
        ).appendTo(productionSection);
        this.fulfillmentGroupSelect = new Select<IFulfillmentGroup>({
            dataSource: this.fulfillmentGroupDataSource,
            optionalText: trans.general.all,
            formatter: (m) => {
                if (Array.isArray(m))
                    return Format.toSimpleCommaSeparatedList(m, (c) => getNameInLocationLanguage(c, true));

                return getNameInLocationLanguage(m, true);
            },
            isMultiSelect: true,
            lazyFetch: true,
            sortBy: (items) => {
                return items
                    .sort((item1, item2) => {
                        const item1Name = Format.translate(
                            item1.name,
                            OperatorClient.context.settings?.location?.language!
                        );
                        const item2Name = Format.translate(
                            item2.name,
                            OperatorClient.context.settings?.location?.language!
                        );
                        return item1Name > item2Name ? 1 : -1;
                    })
                    .sort((item1, item2) => {
                        const order1 = item1.order ?? 999999999;
                        const order2 = item2.order ?? 999999999;

                        if (order1 === order2) {
                            return 0;
                        }

                        if (order1 > order2) return 1;

                        return -1;
                    });
            },
        }).appendTo(row);

        row = $(
            '<div class="formrow"><div class="label">' + trans.settings.productionTileViewMode + "</div></div>"
        ).appendTo(productionSection);
        this.productionTileViewModeSelect = new Select<ProductionTileViewLinkableEntity>({
            dataSource: this.productionTileViewModeDataSource,
            isMultiSelect: false,
            lazyFetch: false,
        }).appendTo(row);

        row = $(
            '<div class="formrow"><div class="label">' + trans.settings.fulfilmentTileSize + "</div></div>"
        ).appendTo(productionSection);
        this.fulfilmentTileSizeSelect = new Select<FulfilmentTileSizeLinkableEntity>({
            dataSource: this.fulfilmentTileSizeDataSource,
            isMultiSelect: false,
            lazyFetch: false,
        }).appendTo(row);

        row = $(
            '<div class="formrow"><div class="label">' + trans.settings.newOrdersViewMode + "</div></div>"
        ).appendTo(productionSection);
        this.newOrdersViewModeSelect = new Select<NewOrdersViewLinkableEntity>({
            dataSource: this.newOrdersModeDataSource,
            isMultiSelect: false,
            lazyFetch: false,
        }).appendTo(row);

        row = $('<div class="formrow"><div class="label">' + trans.settings.status + "</div></div>").appendTo(
            productionSection
        );
        this.orderStateSelect = new Select<OrderStateModeLinkableEntity>({
            dataSource: this.orderStateModeDataSource,
            isMultiSelect: false,
            lazyFetch: false,
        }).appendTo(row);

        const notificationsSection = $(
            `<div class="section"><div class="sectiontitle">${trans.settings.notifications}</div></div>`
        ).appendTo(this.$settings);

        row = $(`<div class="formrow"><div class="label">${trans.settings.playSound}</div></div>`).appendTo(
            notificationsSection
        );

        this.operatorPlaySoundModeSelector = new Select<OperatorPlaySoundModeLinkableEntity>({
            dataSource: this.operatorPlaySoundModeDataSource,
            isMultiSelect: true,
            lazyFetch: false,
            optionalText: trans.general.none,
            formatter: (m) => {
                if (Array.isArray(m)) return Format.toSimpleCommaSeparatedList(m, (c) => c.name);

                return m.name;
            },
        }).appendTo(row);

        this.$actions = $('<div class="actions" />').appendTo(this.$form);

        this.$cancelAction = Ripple.bindRippleOut(
            $('<button class="grey button">' + trans.general.cancel + "</button>").appendTo(this.$actions)
        );
        this.$saveAction = Ripple.bindRippleOut(
            $('<button class="green button" type="submit">' + trans.settings.save + "</button>").click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.$form.submit();
            })
        ).appendTo(this.$actions);
    }

    protected override onVisibilityChanged(isVisible: boolean) {
        if (!isVisible) return;

        this.isReady = false;
        this.locationSelect.fetchOptions().then(() => {
            this.locationSelect.setValue(OperatorClient.context.settings?.location);
            this.onLocationChanged(OperatorClient.context.settings?.location);
        });

        this.preventClose = OperatorClient.context.hasMissingSettings();
        this.updateCancelBtn();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private onLocationChanged(newLocation?: ILocation) {
        this.isReady = false;
        const loadPromises = new Array<Promise<void>>();

        loadPromises.push(
            this.roomPlanSelect.fetchOptions().then(() => {
                this.roomPlanSelect.setValue(OperatorClient.context.settings?.planRoom);
            })
        );

        loadPromises.push(
            this.orderRoomsSelect.fetchOptions().then(() => {
                this.orderRoomsSelect.setValues(OperatorClient.context.settings?.orderRooms!);
            })
        );

        loadPromises.push(
            this.fulfillmentGroupSelect.fetchOptions().then(() => {
                this.fulfillmentGroupSelect.setValues(OperatorClient.context.settings?.fulfillmentGroups!);
            })
        );

        loadPromises.push(
            this.productionTileViewModeSelect.fetchOptions().then(() => {
                const mode = OperatorClient.context.settings?.productionTileViewMode || ProductionTileViewMode.Detailed;
                const value: ProductionTileViewLinkableEntity = {
                    id: ProductionTileViewMode[mode],
                    name: trans.enums.productionTileViewMode[ProductionTileViewMode[mode]],
                    value: mode,
                    appearsIn: [],
                };
                this.productionTileViewModeSelect.setValue(value);
            })
        );

        loadPromises.push(
            this.fulfilmentTileSizeSelect.fetchOptions().then(() => {
                const mode = OperatorClient.context.settings?.fulfilmentTileSize || FulfilmentTileSize.Normal;
                const value: FulfilmentTileSizeLinkableEntity = {
                    id: FulfilmentTileSize[mode],
                    name: trans.enums.fulfilmentTileSize[FulfilmentTileSize[mode]],
                    value: mode,
                    appearsIn: [],
                };
                this.fulfilmentTileSizeSelect.setValue(value);
            })
        );

        loadPromises.push(
            this.newOrdersViewModeSelect.fetchOptions().then(() => {
                const mode = OperatorClient.context.settings?.newOrdersViewMode || NewOrdersViewMode.AtTheTop;
                const value: NewOrdersViewLinkableEntity = {
                    id: NewOrdersViewMode[mode],
                    name: trans.enums.newOrdersViewMode[NewOrdersViewMode[mode]],
                    value: mode,
                    appearsIn: [],
                };
                this.newOrdersViewModeSelect.setValue(value);
            })
        );

        loadPromises.push(
            this.orderStateSelect.fetchOptions().then(() => {
                const mode = OperatorClient.context.settings?.orderStateMode || OrderStateMode.All;
                const value: OrderStateModeLinkableEntity = {
                    id: OrderStateMode[mode],
                    name: trans.enums.orderStateMode[OrderStateMode[mode]],
                    value: mode,
                    appearsIn: [],
                };
                this.orderStateSelect.setValue(value);

                this.operatorPlaySoundModeSelector.setValues(
                    OperatorClient.context.settings?.operatorPlaySoundModesList?.map((mode) => {
                        return {
                            id: OperatorPlaySoundMode[mode],
                            name: trans.enums.operatorPlaySoundMode[OperatorPlaySoundMode[mode]],
                            value: mode,
                        };
                    }) ?? []
                );
            })
        );

        Promise.all(loadPromises).then(() => {
            this.isReady = true;
        });
    }

    protected override beforeResize(width: number, height: number) {
        const fullScreenSize = {
            height: height - 80,
        };

        if (fullScreenSize.height > 750) fullScreenSize.height = 750;

        this.$body.css(fullScreenSize);
    }

    private onSave() {
        if (!this.isReady) return;

        const data: ISetOperatorSettingsCmd = {
            fulfillmentGroupIds: this.fulfillmentGroupSelect.getValues().map((d) => d.id),
            locationId: this.locationSelect.getValue()?.id!,
            orderRoomIds: this.orderRoomsSelect.getValues().map((d) => d.id),
            planRoomId: this.roomPlanSelect.getValue()?.id,
            productionTileViewMode: this.productionTileViewModeSelect.getValue()!.value,
            newOrdersViewMode: this.newOrdersViewModeSelect.getValue()!.value,
            fulfilmentTileSize: this.fulfilmentTileSizeSelect.getValue()!.value,
            orderStateMode: this.orderStateSelect.getValue()!.value,
            operatorPlaySoundModes: this.operatorPlaySoundModeSelector.getValues().map(c => c.value),
        };

        let hasError = false;
        this.$form.find(".haserror").removeClass("haserror");

        if (data.locationId === undefined) {
            this.locationSelect.setError(true);
            hasError = true;
        }

        if (data.planRoomId === undefined) {
            this.roomPlanSelect.setError(true);
            hasError = true;
        }

        if (hasError) return;

        OperatorClient.context
            .changeSettings(data)
            .then(() => {
                this.preventClose = false;
                this.close();
            })
            .catch((ex) => {
                logError(ex);
                Popup.show({
                    title: trans.general.operationFailed,
                    message: trans.settings.failedSave,
                });
            });
    }

    private roomDataSource: IDataSource<IRoom> = {
        getData: (filter: IRoomsQry) => {
            const loc = this.locationSelect.getValue();
            filter.locationId = loc === undefined ? undefined : loc.id;

            return new Promise((resolve, reject) => {
                if (filter.locationId === undefined) {
                    resolve({
                        items: [],
                        limit: filter.limit!,
                        offset: filter.offset!,
                        hasNext: false,
                        hasPrev: false,
                    });
                    return;
                }

                return Factory.getInstance<RoomDao>(RoomDao)
                    .getData(filter)
                    .then((s) => {
                        resolve(s);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            });
        },
    };

    private productionTileViewModeDataSource: IDataSource<ProductionTileViewLinkableEntity> = {
        getData: () => {
            return new Promise((resolve) => {
                const items = Object.keys(ProductionTileViewMode)
                    .filter((x) => isNaN(Number(x)))
                    .map(
                        (x) =>
                            ({
                                name: trans.enums.productionTileViewMode[x],
                                id: x,
                                value: ProductionTileViewMode[x] as ProductionTileViewMode,
                                appearsIn: [],
                            }) as ProductionTileViewLinkableEntity
                    );
                resolve({ items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 });
            });
        },
    };

    private fulfilmentTileSizeDataSource: IDataSource<FulfilmentTileSizeLinkableEntity> = {
        getData: () => {
            return new Promise((resolve) => {
                const items = Object.keys(FulfilmentTileSize)
                    .filter((x) => isNaN(Number(x)))
                    .map(
                        (x) =>
                            ({
                                name: trans.enums.fulfilmentTileSize[x],
                                id: x,
                                value: FulfilmentTileSize[x] as FulfilmentTileSize,
                                appearsIn: [],
                            }) as FulfilmentTileSizeLinkableEntity
                    );
                resolve({ items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 });
            });
        },
    };

    private newOrdersModeDataSource: IDataSource<NewOrdersViewLinkableEntity> = {
        getData: () => {
            return new Promise((resolve) => {
                const items = Object.keys(NewOrdersViewMode)
                    .filter((x) => isNaN(Number(x)))
                    .map(
                        (x) =>
                            ({
                                name: trans.enums.newOrdersViewMode[x],
                                id: x,
                                value: NewOrdersViewMode[x] as NewOrdersViewMode,
                                appearsIn: [],
                            }) as NewOrdersViewLinkableEntity
                    );
                resolve({ items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 });
            });
        },
    };

    private orderStateModeDataSource: IDataSource<OrderStateModeLinkableEntity> = {
        getData: () => {
            return new Promise((resolve) => {
                const items = Object.keys(OrderStateMode)
                    .filter((x) => isNaN(Number(x)))
                    .map(
                        (x) =>
                            ({
                                name: trans.enums.orderStateMode[x],
                                id: x,
                                value: OrderStateMode[x] as OrderStateMode,
                                appearsIn: [],
                            }) as OrderStateModeLinkableEntity
                    );
                resolve({ items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 });
            });
        },
    };

    private operatorPlaySoundModeDataSource: IDataSource<OperatorPlaySoundModeLinkableEntity> = {
        getData: () => {
            return new Promise((resolve) => {
                const items = Object.keys(OperatorPlaySoundMode)
                    .filter((x) => isNaN(Number(x)))
                    .map(
                        (x) =>
                            ({
                                name: trans.enums.operatorPlaySoundMode[x],
                                id: x,
                                value: OperatorPlaySoundMode[x] as OperatorPlaySoundMode,
                            }) as OperatorPlaySoundModeLinkableEntity
                    );
                resolve({ items: items, hasPrev: false, hasNext: false, limit: 0, offset: 0 });
            });
        },
    };

    private fulfillmentGroupDataSource: IDataSource<IFulfillmentGroup> = {
        getData: (filter: IFulfillmentGroupsQry) => {
            const loc = this.locationSelect.getValue();
            filter.locationId = loc === undefined ? undefined : loc.id;

            return new Promise((resolve, reject) => {
                if (filter.locationId === undefined) {
                    resolve({
                        items: [],
                        limit: filter.limit!,
                        offset: filter.offset!,
                        hasNext: false,
                        hasPrev: false,
                    });
                    return;
                }

                return Factory.getInstance<CatalogDao>(CatalogDao)
                    .getFulfillmentGroups(filter)
                    .then((s) => {
                        resolve(s);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            });
        },
    };

    private updateCancelBtn() {
        if (this.preventClose) {
            this.$cancelAction
                .html(trans.navigation.logout)
                .unbind("click")
                .bind("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    OperatorClient.context
                        .signOut()
                        .then(() => {
                            location.href = "/";
                        })
                        .catch((ex) => {
                            logError(ex);
                            location.href = "/";
                        });
                });
        } else {
            this.$cancelAction
                .html(trans.general.cancel)
                .unbind("click")
                .bind("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    this.close();
                });
        }
    }
}

interface ProductionTileViewLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: ProductionTileViewMode;
}

interface NewOrdersViewLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: NewOrdersViewMode;
}

interface FulfilmentTileSizeLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: FulfilmentTileSize;
}

interface OrderStateModeLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    appearsIn?: ILinkableEntity[];
    value: OrderStateMode;
}

interface OperatorPlaySoundModeLinkableEntity extends ILinkableEntity {
    id: string;
    name: string;
    value: OperatorPlaySoundMode;
}
